import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from 'react-toastify';
import { Plus, Edit, Trash2, Save, X, List, BarChart3, Upload, Lock, Image, Search } from "lucide-react";
import SubcategoryManager from "./SubcategoryManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return '';
  // If URL is already absolute (starts with http:// or https://), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If URL starts with /api, prepend BACKEND_URL
  if (url.startsWith('/api')) {
    return `${BACKEND_URL}${url}`;
  }
  // Otherwise, prepend API
  return `${API}${url}`;
};

const ContentCategoriesTab = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [managingSubcategoriesFor, setManagingSubcategoriesFor] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      console.log('Fetching categories from:', `${API}/admin/content-categories`);
      const response = await axios.get(`${API}/admin/content-categories`);
      console.log('Categories response:', response.data);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error loading categories');
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/content-categories/${categoryId}`);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Cannot delete category with existing content');
      } else {
        toast.error('Error deleting category');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
        <span className="ml-2 text-burgundy">Loading categories...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Content Categories</h2>
        <Button 
          onClick={() => setShowForm(true)} 
          className="bg-burgundy hover:bg-burgundy/80 text-cream"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      {/* Categories List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <Card key={category.id} className="hover:shadow-lg transition-all duration-300 bg-white border-burgundy/20">
            {category.image_url && (
              <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                <img 
                  src={category.image_url} 
                  alt={category.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg font-serif text-burgundy">
                  {category.title}
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className={category.visible ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'}>
                    {category.visible ? 'Visible' : 'Hidden'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-burgundy/70 mb-4 line-clamp-3">{category.description}</p>
              
              <div className="flex justify-between items-center mb-4">
                <Badge variant="outline" className="border-burgundy text-burgundy">
                  {category.content_count} content items
                </Badge>
                <span className="text-sm text-burgundy/50">
                  Order: {category.order}
                </span>
              </div>

              <div className="flex gap-2 mb-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setEditingCategory(category);
                    setShowForm(true);
                  }}
                  className="flex-1 border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => deleteCategory(category.id)}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <Button 
                size="sm"
                onClick={() => setManagingSubcategoriesFor(category)}
                className="w-full bg-burgundy hover:bg-burgundy/90 text-white font-medium shadow-sm"
              >
                <List className="h-4 w-4 mr-1" />
                Manage Subcategories
              </Button>
            </CardContent>
          </Card>
        ))}

        {categories.length === 0 && (
          <div className="col-span-full text-center py-12">
            <BarChart3 className="h-16 w-16 text-burgundy/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-burgundy/60 mb-2 font-serif">No Categories</h3>
            <p className="text-burgundy/50 mb-4">Create your first content category to organize classroom content.</p>
            <Button 
              onClick={() => setShowForm(true)} 
              className="bg-burgundy hover:bg-burgundy/80 text-cream"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Category
            </Button>
          </div>
        )}
      </div>

      {/* Category Form Modal */}
      {showForm && (
        <ContentCategoryForm
          category={editingCategory}
          onClose={() => {
            setShowForm(false);
            setEditingCategory(null);
          }}
          onSuccess={() => {
            fetchCategories();
            setShowForm(false);
            setEditingCategory(null);
          }}
        />
      )}

      {/* Subcategory Management Modal */}
      {managingSubcategoriesFor && (
        <SubcategoryManager
          category={managingSubcategoriesFor}
          onClose={() => setManagingSubcategoriesFor(null)}
        />
      )}
    </div>
  );
};

// Content Category Form Component
const ContentCategoryForm = ({ category = null, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: category?.title || '',
    description: category?.description || '',
    image_url: category?.image_url || '',
    image_alt: category?.image_alt || '',
    order: category?.order || 0,
    visible: category?.visible !== undefined ? category.visible : true,
    member_only: category?.member_only !== undefined ? category.member_only : false
  });
  const [loading, setLoading] = useState(false);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [imageLibraryImages, setImageLibraryImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const fullImageUrl = `${API}${response.data.image_url}`;
        setFormData({...formData, image_url: fullImageUrl});
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const searchImageLibrary = async (query = '') => {
    try {
      console.log('Category form searching Image Library for:', query);
      setImageLibraryImages([]); // Clear previous results
      
      const response = await axios.get(`${API}/admin/image-library`, {
        params: query ? { search: query, public_access: "true" } : { public_access: "true" }
      });
      
      console.log('Category form Image Library response:', response.data);
      
      // Transform the response to match the expected format
      const transformedImages = response.data.map(image => ({
        id: image.id,
        urls: {
          small: getImageUrl(image.url),
          regular: getImageUrl(image.url)
        },
        alt_description: image.description || image.name,
        name: image.name,
        description: image.description,
        tags: image.tags || []
      }));
      
      setImageLibraryImages(transformedImages);
      
    } catch (error) {
      console.error('Image Library API error:', error);
      setImageLibraryImages([]);
      toast.error('Error loading image library');
    }
  };

  // Load default images when component mounts
  useEffect(() => {
    searchImageLibrary('');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.title.trim()) {
      toast.error('Category title is required');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Category description is required');
      return;
    }

    setLoading(true);

    try {
      // Create clean data object
      const submitData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        image_url: formData.image_url.trim(),
        order: parseInt(formData.order) || 0,
        visible: Boolean(formData.visible),
        member_only: Boolean(formData.member_only)
      };

      if (category) {
        await axios.put(`${API}/admin/content-categories/${category.id}`, submitData);
        toast.success('Category updated successfully!');
      } else {
        await axios.post(`${API}/admin/content-categories`, submitData);
        toast.success('Category created successfully!');
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving category:', error);
      
      // Enhanced error handling
      if (error.response?.status === 400) {
        const errorMessage = error.response.data.detail || 'Category with this title already exists';
        toast.error(errorMessage);
      } else if (error.response?.status === 422) {
        toast.error('Please check your input fields for errors');
      } else if (error.response?.status === 500) {
        toast.error('Server error - please try again or contact support');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Network error - please check your connection and try again');
      } else {
        toast.error('Error saving category - please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-auto bg-white border-burgundy/20 relative z-[10000]">
        <CardHeader>
          <CardTitle className="font-serif text-burgundy flex justify-between items-center">
            {category ? 'Edit Category' : 'Add New Category'}
            <Button variant="ghost" onClick={onClose} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title and Order */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-burgundy mb-2">Category Title</label>
                <Input
                  id="category-title-input"
                  data-testid="category-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="e.g., Hormone Health, Nutrition, Exercise"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Display Order</label>
                <Input
                  type="number"
                  min="0"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                  className="border-burgundy/30 focus:border-gold"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Description</label>
              <Textarea
                id="category-description-input"
                data-testid="category-description-input"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                rows={3}
                placeholder="Brief description of what content this category includes..."
                required
              />
            </div>

            {/* Category Image */}
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Category Image</label>
              <div className="space-y-3">
                {formData.image_url && (
                  <div className="relative">
                    <img 
                      src={formData.image_url} 
                      alt="Category" 
                      className="w-full h-48 object-cover rounded-lg border-2 border-burgundy/20"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData({...formData, image_url: ''})}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {/* File Upload */}
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="category-image-upload"
                      disabled={uploading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('category-image-upload').click()}
                      disabled={uploading}
                      className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-burgundy border-t-transparent rounded-full mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Image
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* URL Input */}
                  <Input
                    type="url"
                    placeholder="Or enter image URL"
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    className="border-burgundy/30 focus:border-gold"
                  />
                  
                  {/* Unsplash Button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowImageLibrary(true)}
                    className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                  >
                    <Image className="h-4 w-4 mr-2" />
                    Image Library
                  </Button>
                </div>
              </div>
            </div>

            {/* Image Alt Text for SEO */}
            {formData.image_url && (
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Image Alt Text (SEO)
                  <span className="text-burgundy/60 font-normal ml-2">- Optional, defaults to category title</span>
                </label>
                <Input
                  type="text"
                  placeholder="Describe the image for search engines"
                  value={formData.image_alt || ''}
                  onChange={(e) => setFormData({...formData, image_alt: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                />
              </div>
            )}

            {/* Visibility Toggle */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="category-visible"
                checked={formData.visible}
                onChange={(e) => setFormData({...formData, visible: e.target.checked})}
                className="rounded border-burgundy/30"
              />
              <label htmlFor="category-visible" className="text-sm font-medium text-burgundy">
                Visible to users
              </label>
            </div>

            {/* Member Only Toggle */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="category-member-only"
                checked={formData.member_only}
                onChange={(e) => setFormData({...formData, member_only: e.target.checked})}
                className="rounded border-burgundy/30"
              />
              <label htmlFor="category-member-only" className="text-sm font-medium text-burgundy flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Member Only (Trial users will see upgrade prompt)
              </label>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading} 
                data-testid="category-submit-button"
                className="bg-burgundy hover:bg-burgundy/80 text-cream"
              >
                {loading ? 'Saving...' : (category ? 'Update Category' : 'Create Category')}
              </Button>
            </div>
          </form>

          {/* Enhanced Image Library Modal */}
          {showImageLibrary && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 10001 }}>
              <Card className="w-full max-w-6xl mx-4 max-h-[90vh] overflow-auto relative" style={{ zIndex: 10002 }}>
                <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-white">
                  <CardTitle className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-serif">Choose the Perfect Image</h3>
                      <p className="text-sm text-white/80">Choose from your uploaded image library</p>
                    </div>
                    <Button variant="ghost" onClick={() => setShowImageLibrary(false)} className="text-white hover:bg-white/20">
                      <X className="h-5 w-5" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="mb-6 space-y-4">
                    <div className="flex space-x-4">
                      <div className="flex-1">
                        <Input
                          id="unsplash-search-input"
                          data-testid="unsplash-search-input"
                          placeholder="Search for images... (e.g., wellness, nutrition, meditation)"
                          onChange={(e) => searchImageLibrary(e.target.value)}
                          className="border-burgundy/30 focus:border-gold text-lg py-3"
                        />
                      </div>
                      <Button 
                        onClick={() => searchImageLibrary(document.getElementById('unsplash-search-input')?.value || 'wellness')}
                        className="bg-burgundy hover:bg-burgundy/80 text-white px-6"
                      >
                        <Search className="h-4 w-4 mr-2" />
                        Search
                      </Button>
                    </div>
                    
                    {/* Popular searches removed for consistency */}
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {imageLibraryImages.length > 0 ? (
                      imageLibraryImages.map((img) => (
                        <div
                          key={img.id}
                          className="group cursor-pointer hover:scale-105 transition-all duration-200 border-2 border-transparent hover:border-gold rounded-lg overflow-hidden"
                          onClick={() => {
                            const imageUrl = img.urls.regular;
                            const imageAlt = img.description || img.alt_description || img.name;
                            setFormData({...formData, image_url: imageUrl, image_alt: imageAlt});
                            setShowImageLibrary(false);
                            toast.success('Image selected successfully!');
                          }}
                        >
                          <div className="relative">
                            <img
                              src={img.urls.small}
                              alt={img.alt_description || 'Wellness image'}
                              className="w-full h-40 object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <div className="bg-white rounded-full p-2">
                                  <Plus className="h-5 w-5 text-burgundy" />
                                </div>
                              </div>
                            </div>
                          </div>
                          {img.photographer && (
                            <div className="p-2 bg-white">
                              <p className="text-xs text-gray-500 truncate">📸 {img.photographer}</p>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="col-span-full text-center py-12">
                        <div className="text-burgundy/50 space-y-4">
                          <Image className="h-16 w-16 mx-auto text-burgundy/30" />
                          <div>
                            <p className="text-lg font-medium text-burgundy">No images found</p>
                            <p className="text-sm">Try searching for: wellness, nutrition, exercise, meditation, or women</p>
                          </div>
                          <Button 
                            onClick={() => searchImageLibrary('wellness')}
                            variant="outline"
                            className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                          >
                            Browse Images
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-burgundy/20">
                    <p className="text-xs text-gray-500 text-center">
                      Select from your image collection
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </div>,
    document.body
  );
};

export default ContentCategoriesTab;
